import { useEffect, useState } from "react";

const MOBILE_BREAKPOINT = 768;
const TOP_VISIBILITY_THRESHOLD = 40;
const DIRECTION_THRESHOLD = 10;

export interface AutoHideHeaderSnapshot {
  hidden: boolean;
  lastY: number;
}

export function getNextAutoHideHeaderSnapshot(
  snapshot: AutoHideHeaderSnapshot,
  scrollY: number,
): AutoHideHeaderSnapshot {
  if (scrollY < TOP_VISIBILITY_THRESHOLD) {
    return { hidden: false, lastY: scrollY };
  }

  const delta = scrollY - snapshot.lastY;
  if (Math.abs(delta) < DIRECTION_THRESHOLD) return snapshot;
  if (delta > 0) return { hidden: true, lastY: scrollY };
  return { hidden: false, lastY: scrollY };
}

export function useAutoHideHeader(enabled: boolean): boolean {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHidden(false);
      return undefined;
    }

    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    let snapshot: AutoHideHeaderSnapshot = { hidden: false, lastY: window.scrollY };

    const syncVisibility = () => {
      if (!mediaQuery.matches) {
        snapshot = { hidden: false, lastY: window.scrollY };
        setHidden(false);
        return;
      }

      const nextSnapshot = getNextAutoHideHeaderSnapshot(snapshot, window.scrollY);
      snapshot = nextSnapshot;
      setHidden((currentHidden) => (currentHidden === nextSnapshot.hidden ? currentHidden : nextSnapshot.hidden));
    };

    syncVisibility();
    window.addEventListener("scroll", syncVisibility, { passive: true });
    mediaQuery.addEventListener("change", syncVisibility);

    return () => {
      window.removeEventListener("scroll", syncVisibility);
      mediaQuery.removeEventListener("change", syncVisibility);
    };
  }, [enabled]);

  return hidden;
}
